import { useState } from 'react';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { renderBadgePng } from '../../../../../common/badges/badge-png';
import { BadgeDesign } from '../../../../../common/badges/badge-svg';
import { AchievementDraft, isThresholdBased } from './achievement-model';

interface SaveAchievementRequest {
  achievementId: string | null;
  name: string;
  description: string | null;
  metric: string;
  reportFieldId: string | null;
  isEnabled: boolean;
  isDiscoverable: boolean;
  showProgress: boolean;
  customMessage: string | null;
  levels: {
    achievementLevelId: string | null;
    name: string;
    threshold: number | null;
    badgeDesign: object | null;
    certificateTemplateId: string | null;
  }[];
}

interface SaveAchievementResponse {
  achievementId: string;
  levelIds: string[];
}

export const useSaveAchievement = () => {
  const [saving, setSaving] = useState(false);
  const saveFetch = useEndpointFetch<SaveAchievementRequest, SaveAchievementResponse>(
    '/vm/admin-console/achievement/save'
  );
  const badgeImageFetch = useEndpointFetch<{ achievementLevelId: string; badgeImage: File }>(
    '/vm/admin-console/achievement/badge-image',
    { formData: true }
  );

  const save = async (draft: AchievementDraft): Promise<{ ok: boolean; message?: string }> => {
    setSaving(true);
    try {
      const thresholdBased = isThresholdBased(draft.metric);
      const response = await saveFetch.run({
        achievementId: draft.achievementId,
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        metric: draft.metric,
        reportFieldId: draft.metric === 'report_field_total' ? draft.reportFieldId : null,
        isEnabled: draft.isEnabled,
        isDiscoverable: draft.isDiscoverable,
        showProgress: thresholdBased && draft.showProgress,
        customMessage: draft.customMessage.trim() || null,
        levels: draft.levels.map((level) => ({
          achievementLevelId: level.achievementLevelId,
          name: level.name.trim(),
          threshold: thresholdBased ? Number(level.threshold) : null,
          badgeDesign: level.badgeDesign,
          certificateTemplateId: level.certificateTemplateId || null,
        })),
      });

      if (!response.ok) {
        return { ok: false, message: response.data?.message || 'Unable to save achievement' };
      }

      const { levelIds } = response.data.data;
      const changed = draft.levels
        .map((level, index) => ({ level, levelId: levelIds[index] }))
        .filter(
          ({ level, levelId }) =>
            levelId &&
            level.badgeDesign &&
            (!level.hasImage || JSON.stringify(level.badgeDesign) !== level.originalDesign)
        );

      const uploadsOk = await changed.reduce<Promise<boolean>>(
        (previous, { level, levelId }) =>
          previous.then(async (ok) => {
            if (!ok) return false;
            const blob = await renderBadgePng(level.badgeDesign as BadgeDesign);
            const upload = await badgeImageFetch.run({
              achievementLevelId: levelId,
              badgeImage: new File([blob], 'badge.png', { type: 'image/png' }),
            });
            return upload.ok;
          }),
        Promise.resolve(true)
      );

      if (!uploadsOk) {
        return { ok: false, message: 'Achievement saved, but a badge image failed to upload. Save again to retry.' };
      }

      return { ok: true };
    } catch (e) {
      return { ok: false, message: 'Unable to save achievement' };
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
};
